/**
 * Pure helpers for the HistoryPanel. The hooks layer handles fetching +
 * mutating; these helpers shape display + perform the local-state mirror
 * (matching BE history entries against the local `images[]` carousel).
 */

import type {
  HistoryEntryResponse,
  ImageLabOperationType,
  ResultImageInfo,
} from '../../../domain';

/**
 * Friendly per-op-kind label for history rows. Mirrors the catalog name
 * but falls back to the raw kind when the catalog doesn't know it
 * (defensive against BE rolls forward).
 */
const OPERATION_LABELS: Record<string, string> = {
  TextToImage: 'Generate',
  ImageToImage: 'Transform',
  Inpaint: 'Inpaint',
  Outpaint: 'Expand canvas',
  RemoveBackground: 'Remove background',
  RemoveObject: 'Remove object',
  Upscale: 'Upscale',
  Enhance: 'Enhance',
  ColorCorrect: 'Color correct',
  ColorGrade: 'Color grade',
  StyleTransfer: 'Style transfer',
  FaceEnhance: 'Face enhance',
  FaceSwap: 'Face swap',
  Variations: 'Variations',
  Zoom: 'Zoom',
  Denoise: 'Denoise',
  Deblur: 'Deblur',
  Colorize: 'Colorize',
  SketchToImage: 'Sketch to image',
  TryOn: 'Try on',
  MultiAngle: 'Multi-angle',
  ConsistencySheet: 'Consistency sheet',
};

export function labelForOperation(kind: ImageLabOperationType): string {
  return OPERATION_LABELS[kind] ?? kind;
}

/**
 * Short relative time label for history rows. Returns "just now" /
 * "Xm ago" / "Xh ago" / "Xd ago" / fall back to a locale time string.
 * Defensive against unparseable input — returns the raw input on parse
 * failure so the row stays renderable.
 */
export function relativeTimeLabel(iso: string, now: Date = new Date()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffMs = now.getTime() - t;
  if (diffMs < 0) return 'just now';
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 30) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return new Date(t).toLocaleDateString();
}

/**
 * Given a history entry (or null for the blank initial state) and the
 * local `images[]` carousel, find the matching local image index.
 *
 * Returns -1 when:
 * - The entry's `afterImageId` doesn't appear in the local carousel
 *   (history references an image we don't have locally — typical after
 *   page reload before the session is refreshed).
 * - `entry` is null and there's no obvious "initial state" to land on.
 */
export function indexForHistoryEntry(
  entry: HistoryEntryResponse | null,
  images: ResultImageInfo[],
): number {
  if (!entry) return -1;
  return images.findIndex(i => i.id === entry.afterImageId);
}

/**
 * Pick the history entry to highlight as "current" in the panel. The BE
 * tracks `currentIndex`; this maps it back to the matching entry. Returns
 * null when there's no valid entry (e.g., blank initial state).
 */
export function currentHistoryEntry(
  history: HistoryEntryResponse[],
  currentIndex: number,
): HistoryEntryResponse | null {
  if (currentIndex < 0 || currentIndex >= history.length) return null;
  return history[currentIndex] ?? null;
}
